import { forwardRef, type InputHTMLAttributes, type ReactNode, type WheelEvent } from "react";
import { cn } from "@/lib/cn";
import { FILTER_ACTIVE_CLASS, useFilterHighlight } from "@/components/molecules/FilterBar";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leading?: ReactNode;
  trailing?: ReactNode;
}

const inputResetAutofill =
  /* Chrome applies a solid autofill background on the input; with a transparent field inside a tinted wrapper it reads as a “ghost” behind the text. */
  [
    "transition-[background-color,box-shadow] duration-[100000s] delay-0",
    "[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_rgba(255,255,255,0.72)]",
    "[&:-webkit-autofill:hover]:shadow-[inset_0_0_0_1000px_rgba(255,255,255,0.72)]",
    "[&:-webkit-autofill:focus]:shadow-[inset_0_0_0_1000px_rgba(255,255,255,0.72)]",
    "[&:-webkit-autofill:active]:shadow-[inset_0_0_0_1000px_rgba(255,255,255,0.72)]",
    "[&:-webkit-autofill]:[-webkit-text-fill-color:theme(colors.bridge.fg)]",
  ].join(" ");

/** Browsers increment focused number inputs on wheel — block that so scroll doesn't mutate values. */
function preventNumberWheelChange(e: WheelEvent<HTMLInputElement>) {
  e.currentTarget.blur();
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leading, trailing, className, type, onWheel, value, ...rest },
  ref,
) {
  const filterActive = useFilterHighlight(value != null && String(value) !== "");
  return (
    <div
      className={cn(
        "group flex items-center gap-2 h-10 px-3 rounded-md bg-bridge-surface/70 border border-bridge-border/70",
        "transition-colors focus-within:border-bridge-accent/60 focus-within:shadow-ring",
        className,
        filterActive && FILTER_ACTIVE_CLASS,
      )}
    >
      {leading && (
        <span className={cn("shrink-0", filterActive ? "text-bridge-accent" : "text-bridge-muted")}>
          {leading}
        </span>
      )}
      <input
        ref={ref}
        type={type}
        value={value}
        className={cn(
          "min-w-0 flex-1 bg-transparent outline-none text-sm text-bridge-fg placeholder:text-bridge-subtle",
          inputResetAutofill,
        )}
        onWheel={(e) => {
          if (type === "number") preventNumberWheelChange(e);
          onWheel?.(e);
        }}
        {...rest}
      />
      {trailing && <span className="text-bridge-muted shrink-0">{trailing}</span>}
    </div>
  );
});
